import { useCallback, useContext, useEffect, useState } from 'react'
import { CgGym } from 'react-icons/cg'
import { FiCalendar, FiChevronRight } from 'react-icons/fi'

import { getMyLicensePurchases } from '~/modules/sales/licenseExtensions/services'
import { LicenseExtensionPurchase } from '~/modules/sales/licenseExtensions/types'
import { numberToBRL, translatePaymentInterval } from '~/modules/sales/services'
import { Subscription } from '~/modules/sales/types'
import { getLocalDate } from '~/utils/getLocalDate'

import { subscriptionsContext } from '../..'
import { SubscriptionModal } from '../ActiveSubscription/SubscriptionModal'
import { PurchaseHistoryTable } from '../LicenseExtensions/PurchaseHistoryTable'

export const PurchaseHistory: React.FC = () => {
  const { subscription, isValid } = useContext(subscriptionsContext)
  const [purchases, setPurchases] = useState<LicenseExtensionPurchase[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)

  const fetchPurchases = useCallback(() => {
    setLoading(true)
    getMyLicensePurchases()
      .then(setPurchases)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchPurchases()
  }, [fetchPurchases])

  return (
    <div className='flex flex-col gap-8'>
      {/* Subscription enrollment */}
      <section className='flex flex-col gap-3'>
        <h3 className='font-semibold text-lg'>Assinatura</h3>

        {!subscription ? (
          <p className='text-sm text-slate-500'>Nenhuma assinatura encontrada.</p>
        ) : (
          <div className='flex items-center gap-4 border border-slate-200 rounded-xl p-4 bg-white'>
            <div className='p-2 bg-gpa-blue-50 rounded-lg text-gpa-blue-500'>
              <CgGym size={24} />
            </div>

            <div className='flex flex-col flex-1 gap-0.5'>
              <div className='flex items-center gap-2'>
                <p className='font-semibold'>
                  {subscription.plan?.name ?? subscription.metaData.planName}
                </p>
                {!isValid && (
                  <span className='px-2 py-0.5 text-xs font-semibold text-gpa-red bg-red-100 bg-opacity-20 rounded-md border border-red-200'>
                    {subscription.status === 'canceled'
                      ? 'Cancelada'
                      : new Date(subscription.termEnd) < new Date()
                      ? 'Expirada/Atrasada'
                      : 'Inativa'}
                  </span>
                )}
              </div>
              <div className='flex items-center gap-3 text-sm text-slate-500'>
                <span className='flex items-center gap-1'>
                  <FiCalendar size={12} />
                  Desde {getLocalDate(subscription.createdAt)}
                </span>
                <span>&bull;</span>
                <span>
                  Renovação em {getLocalDate(subscription.termEnd)}
                </span>
                <span>&bull;</span>
                <span>
                  {translatePaymentInterval(
                    subscription.plan?.interval,
                    subscription.plan?.intervalCount,
                  )}
                </span>
              </div>
              {subscription.plan && (
                <p className='text-sm text-slate-500'>
                  {numberToBRL(
                    subscription.plan.metaData.items.reduce(
                      (s, i) =>
                        s + i.quantity * (i.pricing_scheme?.price ?? i.price ?? i.amount ?? 0),
                      0,
                    ) / 100,
                  )}
                </p>
              )}
            </div>

            <button
              type='button'
              onClick={() => setModalOpen(true)}
              className='flex items-center gap-1 px-3 py-2 rounded-lg text-sm
                font-medium text-gpa-blue-500 hover:bg-gpa-blue-50
                transition-colors duration-150'
            >
              Ver detalhes
              <FiChevronRight size={14} />
            </button>
          </div>
        )}
      </section>

      {/* License extension purchase history */}
      <section className='flex flex-col gap-3'>
        <h3 className='font-semibold text-lg'>Licenças Avulsas</h3>

        {loading ? (
          <div className='flex justify-center py-8'>
            <div className='h-8 w-8 border-[6px] rounded-full border-r-gpa-blue-50 border-gpa-blue-500 animate-spin' />
          </div>
        ) : (
          <PurchaseHistoryTable purchases={purchases} />
        )}
      </section>

      <SubscriptionModal visible={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}
